import { useState, FC, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  LinearProgress,
  Avatar,
  Button,
  Alert,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';

import { healthWalletApi, useGetLandingQuery, useGetOnboardingStatusQuery, useRequestHealthDataMutation } from './healthWalletSlice';
import { BRAND, BRAND_DARK, BRAND_DARKER, BG } from './healthWalletConstants';
import { Chatbot } from './Chatbot';
import { useAppDispatch, useAuthProfile } from "../../app/hooks";
import { AppLayout } from '../../App';
import { DataRequestSSEUpdates } from './DataRequestSSEUpdates';
import { clearSyncLog, useSyncRunning } from './healthWalletSyncService';
import { HealthWalletSettings } from "./HealthWalletSettings";
import { HWRecordRendererV1 } from './HWRecordRendererV1';
import { ProviderOrgLists, ProviderRefreshButton, providerRetrieveLabel, OrgDetailDialog } from './ProviderOrgRow';
// Delete affordance for a provider the patient added by mistake — self-hides unless it is their own
// pick with nothing retrieved. See project_hw_epic_direct_connect.
import { ProviderRemoveButton } from './ProviderRemoveButton';
import type { OrgDetail } from './ProviderOrgRow';
import type { EpicFhirOrg } from '../../types/onboarding';


// Records screen sizes to the viewport (small % gutter on larger screens) rather than a
// fixed MUI breakpoint cap. Every Container on this screen must use this so the header
// content, chips, banners, and data list stay horizontally aligned (the renderers carry
// a matching copy).
const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };

// --- Main Component ---
// HealthWalletRecords is the post-onboarding HOME SHELL (2026-07-12 split, Leo): it owns the
// header (patient banner, Ask Polly, settings button), the SYNC lifecycle UI (sync card + SSE,
// refresh strip, outcome banner), the "Your providers" card, Settings, and the Chatbot — the
// wallet's PLUMBING, common to any rendering of the records. The records RENDERING itself is
// delegated to a SWAPPABLE, propless renderer (HWRecordRenderer — the evolving surface — or
// HWRecordRendererV1, the frozen baseline), chosen by the Settings "Advanced" toggle. The
// renderers own their own slice hooks; RTK dedupes them against the shell's reads.

export const HealthWalletRecords: FC = () => {
  const { profile: loggedInUserProfile, authStatus, isLoading: isLoadingLoggedInUserProfile, isSuccess: isSuccessLoggedInUserProfile, isError: isErrorLoggedInUserProfile } = useAuthProfile();
  const [detailOrg, setDetailOrg] = useState<OrgDetail | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // GLOBAL chat engine (Leo: siblings design) — the Chatbot + conversation state live HERE so history
  // survives renderer swaps and the widget can sit anywhere in the shell. The renderers own only the
  // Ask-Polly BAR and talk up via callbacks; aiContext = the active tab they report (→ contextHint).
  const [chatOpen, setChatOpen] = useState(false);
  const [aiContext, setAiContext] = useState('conditions'); // matches the renderer's default tab
  const pendingChatQueryRef = useRef('');
  const handleAskPolly = (query: string) => {
    pendingChatQueryRef.current = query;
    setChatOpen(true);
  };
  const [showProviders, setShowProviders] = useState(false); // "Your providers" card — collapsed on open (Leo), Show/Hide toggle

  const [showSyncLog, setShowSyncLog] = useState(false);
  const syncLogInitialized = useRef(false);

  const isAuthenticated = !isLoadingLoggedInUserProfile && isSuccessLoggedInUserProfile && authStatus === 'authenticated';
  const skip = isLoadingLoggedInUserProfile || !isSuccessLoggedInUserProfile || authStatus !== 'authenticated';

  // Landing — the shell reads it only for the patient header + the showData gate; the renderer
  // makes its own (RTK-deduped) call for counts/notes. ONE fetch either way.
  const { data: landing, isLoading: isLandingLoading } = useGetLandingQuery(undefined, { skip });
  // isOnboardingLoading is only true on initial page load — never re-triggered after — so it safely guards
  // status-dependent sections (pending/failed/idle) without transient flashes.
  const { data: onboardingStatus, isLoading: isOnboardingLoading } = useGetOnboardingStatusQuery(undefined, { skip });

  // useSyncRunning() does two jobs: (1) subscribes to the SSE stream, which keeps the ONE
  // connection open while the records screen is mounted (RTK ref-counted), so a workflow
  // completing — from any trigger, incl. an Epic redirect+reload or another tab — refetches
  // the dashboard; (2) returns the DURABLE running flag derived from dataRequestStatus (not
  // the SSE event buffer, which is empty on reload), which drives the "Refreshing…" cue below.
  const isSyncing = useSyncRunning();

  const dispatch = useAppDispatch();
  const [requestHealthData] = useRequestHealthDataMutation();

  // First run: show log + progress bar. Refresh: keep showing live records, no sync card.
  // Initialized once so the toggle button can still override it.
  useEffect(() => {
    if (!syncLogInitialized.current && onboardingStatus !== undefined) {
      syncLogInitialized.current = true;
      setShowSyncLog(onboardingStatus.lastCompletedAt === null);
    }
  }, [onboardingStatus]);

  const dataRequestStatus = onboardingStatus?.dataRequestStatus;
  const isDataRequestPending = dataRequestStatus === 'pending' || dataRequestStatus === 'processing';
  const isDataRequestFailed = dataRequestStatus === 'failed';

  // Phase 3 outcome signals (records-screen banner). hasRecords excludes self-uploads;
  // dataRequestError is the failure reason (diagnosable); noRecordsReason picks the info message.
  const hasRecords = onboardingStatus?.hasRecords === true;
  const dataRequestError = onboardingStatus?.dataRequestError ?? null;
  const noRecordsReason = onboardingStatus?.noRecordsReason ?? null;
  // Discovered providers (same data onboarding/Settings use) — surfaced here so the user sees connection
  // status + can reconnect any needing re-auth. Rendered via the shared ProviderOrgRow.
  const epicOrgs = onboardingStatus?.epicFhirOrgs ?? [];
  const kno2Orgs = onboardingStatus?.kno2Orgs ?? [];
  // Banner shows when the last request is terminal (not in-flight) AND either it failed, or it
  // completed but the network found nothing for this patient. Never shown while syncing.
  const showOutcomeBanner =
    isAuthenticated && !isOnboardingLoading && !isDataRequestPending &&
    (isDataRequestFailed || (dataRequestStatus === 'completed' && !hasRecords));

  // Retry = re-run the IAS/network sync. NO click-time status patch (it kept the OLD dataRequestId →
  // forged the SSE snapshot merge's race signature → spurious refetch). The response's full-state
  // assign below is status-ATOMIC-with-id and carries the DB stopwatch seed; the banner/progress flip
  // when it lands (~1 round-trip). See project_hw_seed_optimistic_interaction.
  const handleRetry = async () => {
    clearSyncLog(dispatch); // wipe the old run's progress log immediately on click
    try {
      const result = await requestHealthData().unwrap();
      if (result?.state) {
        dispatch(healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          if (draft) Object.assign(draft, result.state);
        }));
      }
    } catch (err) {
      console.error('[HealthWalletRecords] retry failed:', err);
      dispatch(healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        if (draft) draft.dataRequestStatus = 'failed';
      }));
    }
  };

  // Post-onboarding per-org Epic REFRESH — SAME behavior as Settings (no Connect/Reconnect/Skip here).
  // Token-first: try the stored token via requestHealthData({type:'epic', iasRequestId, orgIndex}); the
  // backend refreshes it and starts the workflow if valid. Only if it returns needs_auth do we fall back to
  // the org-scoped MyChart OAuth redirect. One "Refresh" button covers both.
  const handleRefreshOrg = async (org: EpicFhirOrg) => {
    clearSyncLog(dispatch);
    // NO click-time status patch (stale-id forgery — see handleRetry note / project_hw_seed_optimistic_interaction).
    // The response assign is status-atomic-with-id: authenticated → {pending, run id};
    // needs_auth → {auth_pending, intent row id}.
    let intentRequestId: string | null = null;
    try {
      const result = await requestHealthData({ type: 'epic', ...(org.requestId ? { iasRequestId: org.requestId, orgIndex: org.index } : {}) }).unwrap();
      if (result?.state) {
        dispatch(healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          if (draft) Object.assign(draft, result.state);
        }));
      }
      if (result?.state?.epicAuthStatus !== 'needs_auth') return; // token valid/refreshed → workflow started
      intentRequestId = result?.state?.dataRequestId ?? null; // the pre-created auth_pending intent row
    } catch (e) {
      console.error('[HealthWalletRecords] epic refresh failed', e);
    }
    // needs_auth (or error) → org-scoped MyChart OAuth redirect.
    if (!loggedInUserProfile?.id) return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/healthwallet/onboarding/verify';
    form.style.display = 'none';
    const add = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = name; input.value = value;
      form.appendChild(input);
    };
    add('id', String(loggedInUserProfile.id));
    add('type', 'epic');
    add('iasRequestId', String(org.requestId ?? ''));
    add('orgIndex', String(org.index));
    // Thread the pre-created intent row's id → OAuth state → exchange-code promotes THAT row.
    if (intentRequestId) add('intentRequestId', String(intentRequestId));
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  // Trailing for a provider row (POST-onboarding): the aggregate outcome chip + a single token-first Refresh
  // button (mirrors Settings). No Connect/Reconnect/Skip — Refresh handles a needs_auth org via redirect.
  const providerTrailing = (org: EpicFhirOrg) => (
    <>
    {/* Only renders for a provider the PATIENT added that has retrieved nothing — which on this
        screen is rare by definition, but a mis-pick that connected and found nothing lands here. */}
    <ProviderRemoveButton org={org} disabled={isDataRequestPending} />
    <ProviderRefreshButton
      disabled={isDataRequestPending}
      // Was unlabelled, so a not-yet-connected or SKIPPED org here read as a plain "Get Health
      // Records" — a retrieval it cannot do without a MyChart login first. Same rule as Settings.
      label={providerRetrieveLabel(org)}
      onClick={() => handleRefreshOrg(org)}
    />
    </>
  );

  // Show data content whenever we have cached dashboard data, regardless of whether a refresh is in flight.
  const showData = !!landing && !isOnboardingLoading;
  // Show the inline sync card only on the first run (no prior completed data).
  // During a refresh, live data stays visible — sync card is suppressed.
  const isFirstRun = onboardingStatus !== undefined && onboardingStatus.lastCompletedAt === null;
  // First-run sync card is now PENDING-only (progress + log). Terminal outcomes (failed /
  // no-records) are handled uniformly by the outcome banner below, first run or refresh.
  const showSyncCard = isAuthenticated && !isOnboardingLoading && isDataRequestPending && isFirstRun;

  // Landing-derived (header only — counts/lists live in the renderer)
  const patient = landing?.patient;
  const patientName = patient?.name?.[0] ? `${(patient.name[0].given || []).join(' ')} ${patient.name[0].family || ''}`.trim() : 'Patient';
  // Hero bits (design cue: Overview Desktop.dc.html): greeting is first-name only, plus two
  // compact stats. "Hi, there" while the landing hasn't resolved a name.
  const firstName = patient?.name?.[0]?.given?.[0] || (patientName !== 'Patient' ? patientName.split(' ')[0] : 'there');
  const providerCount = epicOrgs.length + kno2Orgs.length;
  const lastUpdated = (() => {
    const s = onboardingStatus?.lastCompletedAt;
    if (!s) return '—';
    const d = new Date(s); const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const y = new Date(now); y.setDate(now.getDate() - 1);
    if (d.toDateString() === y.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}) });
  })();
  // Providers toggle lives IN the hero (Leo 2026-07-14): desktop = left of the Connected-providers
  // stat; mobile = right under the greeting. The old centered button row below the hero is gone,
  // so the content sits higher when the card is collapsed (its default).
  const providersToggle = isAuthenticated && (epicOrgs.length > 0 || kno2Orgs.length > 0) ? (
    <Button
      size="small"
      variant="outlined"
      onClick={() => setShowProviders((v) => !v)}
      sx={{ textTransform: 'none', color: BRAND_DARKER, borderColor: '#d7d3f0', borderRadius: 50, px: 1.75, fontSize: '0.75rem', whiteSpace: 'nowrap', '&:hover': { borderColor: BRAND_DARKER, bgcolor: 'transparent' } }}
    >
      {showProviders ? 'Hide my providers' : 'Show my providers'}
    </Button>
  ) : null;

  return (
    <AppLayout>
      <Box sx={{ bgcolor: BG, minHeight: '100svh', width: '100%', display: 'flex', flexDirection: 'column', mt: { xs: '-56px', sm: '-64px' } }}>

        {/* Flat hero (design cue: Overview Desktop.dc.html) — no colored banner, dark ink on the
            page bg, so the translucent site menu renders here exactly as on every other page. */}
        <Box component="header" sx={{ pt: { xs: '76px', sm: '92px' }, pb: 1.5, width: '100%', position: 'relative' }}>
          <Container maxWidth={false} sx={PAGE_WIDTH_SX}>
            {/* Never wraps (Leo, mobile): xs drops the avatar (too wide) and stacks the two stats
                as compact right-aligned lines next to the gear; sm+ = avatar + stat columns. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <Avatar alt={patientName} sx={{ display: { xs: 'none', sm: 'flex' }, width: 56, height: 56, bgcolor: BRAND, color: BRAND_DARKER, fontWeight: 700, fontSize: 22 }}>
                  {(isLandingLoading || isLoadingLoggedInUserProfile)
                    ? <CircularProgress size={20} sx={{ color: BRAND_DARKER }} />
                    : firstName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#8e8e93', lineHeight: 1.2 }}>Health Wallet</Typography>
                  <Typography variant="h5" component="h1" noWrap sx={{ fontWeight: 700, color: '#1c1c1e', letterSpacing: '-0.5px', lineHeight: 1.15 }}>
                    Hi, {firstName}
                  </Typography>
                  {/* xs: providers toggle rides right under the greeting */}
                  <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.75 }}>{providersToggle}</Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.75 }, flexShrink: 0 }}>
                {/* sm+: providers toggle sits left of the Connected-providers stat */}
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{providersToggle}</Box>
                {isAuthenticated && onboardingStatus && (
                  <>
                    {/* sm+: two stat columns with a divider */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 2.75 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1c1c1e', lineHeight: 1.25 }}>{providerCount}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#8e8e93' }}>Connected providers</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', borderLeft: '1px solid #e2e2e8', pl: 2.75 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1c1c1e', lineHeight: 1.25 }}>{lastUpdated}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#8e8e93' }}>Last updated</Typography>
                      </Box>
                    </Box>
                    {/* xs: compact stacked stat lines */}
                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                      <Typography sx={{ fontSize: 12, color: '#8e8e93', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                        <Box component="span" sx={{ fontWeight: 700, color: '#1c1c1e' }}>{providerCount}</Box> Connected providers
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#8e8e93', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                        Updated <Box component="span" sx={{ fontWeight: 700, color: '#1c1c1e' }}>{lastUpdated}</Box>
                      </Typography>
                    </Box>
                  </>
                )}
                <IconButton
                  onClick={() => setSettingsOpen(true)}
                  aria-label="Open Health Wallet settings"
                  sx={{
                    color: '#6b6b70',
                    bgcolor: 'white',
                    border: '1px solid #e2e2e8',
                    width: 38, height: 38,
                    '&:hover': { bgcolor: 'white', transform: 'rotate(20deg)', transition: 'transform 0.2s ease' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {(!isAuthenticated || isErrorLoggedInUserProfile) && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: '#1c1c1e' }}>Please sign in to access your Health Wallet</Typography>
              </Box>
            )}

            {isAuthenticated && isOnboardingLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: BRAND_DARKER }} />
              </Box>
            )}

            {/* (Ask Polly bar moved INTO the renderers 2026-07-12 — AI entry point + Chatbot are a
                per-renderer design decision now, and Polly gets the active tab as a context hint.) */}
          </Container>

          {/* Refresh-in-progress cue: a thin line pinned to the bottom edge of the hero
              (position:absolute → no layout shift). Shows for an already-onboarded
              re-sync; the first-run sync card handles first run. isSyncing is the shared SSE signal. */}
          {isAuthenticated && isSyncing && !showSyncCard && (
            <LinearProgress
              aria-label="Refreshing your health records"
              sx={{
                position: 'absolute', left: 0, right: 0, bottom: 0,
                height: 3,
                bgcolor: BRAND,
                '& .MuiLinearProgress-bar': { bgcolor: BRAND_DARKER },
              }}
            />
          )}
        </Box>

        {/* Inline sync card — shown when data is being retrieved or failed */}
        {showSyncCard && (
          <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>

              {isDataRequestPending && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1c1c1e' }}>
                      Retrieving your health records…
                    </Typography>
                    <Button size="small" onClick={() => setShowSyncLog(v => !v)}
                      sx={{ color: BRAND_DARKER, textTransform: 'none', fontSize: '0.72rem', minWidth: 'auto', p: '2px 6px' }}>
                      {showSyncLog ? 'Hide log' : 'Show log'}
                    </Button>
                  </Box>
                  <LinearProgress sx={{ borderRadius: 1, bgcolor: '#efeaff', '& .MuiLinearProgress-bar': { bgcolor: BRAND_DARKER } }} />
                </Box>
              )}

              {/* SSE log */}
              {showSyncLog && (
                <Box sx={{ border: '1px solid #d7d3f0', borderRadius: 2, p: 1.5, bgcolor: '#f7f7fa', maxHeight: 220, overflowY: 'auto' }}>
                  <DataRequestSSEUpdates />
                </Box>
              )}
            </Paper>
          </Container>
        )}

        {/* Data-request OUTCOME banner — persistent (no auto-dismiss → Retry stays reachable),
            never replaces the screen (My Documents/Notes below stay visible). failed → error +
            the diagnosable reason; completed-but-no-records → info, message by reason. */}
        {showOutcomeBanner && (
          <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
            <Alert
              severity={isDataRequestFailed ? 'error' : 'info'}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRetry}
                  sx={{ textTransform: 'none', borderRadius: 50, borderColor: BRAND_DARKER, color: BRAND_DARKER }}
                >
                  Retry
                </Button>
              }
              // Light-purple (DOB-pill family) banner regardless of severity, matching the app's
              // look; the severity-colored icon (red/blue) still signals error vs info at a glance.
              sx={{ borderRadius: 3, bgcolor: '#efeaff', color: '#1c1c1e' }}
            >
              {isDataRequestFailed ? (
                <>
                  We hit a problem retrieving your health records. You can retry, or{' '}
                  <Link component={RouterLink} to="/contact">contact support</Link>.
                  {dataRequestError && (
                    <Typography variant="caption" component="div" sx={{ mt: 0.5, color: 'text.secondary' }}>
                      Details: {dataRequestError}
                    </Typography>
                  )}
                </>
              ) : noRecordsReason === 'no_match' ? (
                <>We couldn’t find you in the connected health network yet — your details didn’t match any records. You can try again.</>
              ) : (
                <>We found your providers, but they didn’t return any documents yet. You can try again.</>
              )}
            </Alert>
          </Container>
        )}

        {/* Your providers — connect status per org, via the shared ProviderOrgRow (same as onboarding).
            Run-results display deferred (aggregation TBD). */}
        {isAuthenticated && showProviders && (epicOrgs.length > 0 || kno2Orgs.length > 0) && (
          <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  {/* Same eyebrow treatment as the tabs' "Your record at a glance" (Leo). */}
                  <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: BRAND_DARKER, textTransform: 'uppercase' }}>
                    Your providers
                  </Typography>
                  <Button size="small" onClick={() => setShowProviders(false)} sx={{ textTransform: 'none', color: BRAND_DARKER }}>
                    Hide
                  </Button>
                </Box>
                <ProviderOrgLists
                  epicOrgs={epicOrgs}
                  kno2Orgs={kno2Orgs}
                  renderEpicTrailing={(org) => providerTrailing(org)}
                  onOrgClick={(org) => setDetailOrg(org)}
                  showMyChart={false}
                />
              </Paper>
          </Container>
        )}

        {/* Per-org detail card (technical details / raw error) — opens on row click. */}
        <OrgDetailDialog open={!!detailOrg} onClose={() => setDetailOrg(null)} org={detailOrg} />

        {/* GLOBAL Chatbot — shell-owned (conversation survives renderer swaps); mounted whenever data
            exists, independent of the sync card. contextHint = the tab the active renderer reports. */}
        {isAuthenticated && showData && (
          <Chatbot
            externalOpen={chatOpen}
            onExternalClose={() => setChatOpen(false)}
            initialQuery={pendingChatQueryRef.current}
            clearQuery={() => { pendingChatQueryRef.current = ''; }}
            contextHint={aiContext}
          />
        )}

        {/* Records rendering (HWRecordRendererV1 — the only renderer now; the pre-V1 HWRecordRenderer +
            its Settings toggle were removed 2026-07-28). Hidden while the first-run sync card shows.
            Owns the slice hooks + the Ask-Polly bar; AI-seam callbacks flow up to the global chat engine. */}
        {isAuthenticated && showData && !showSyncCard && (
          <HWRecordRendererV1 onAskPolly={handleAskPolly} onActiveTabChange={setAiContext} />
        )}
      </Box>
      <HealthWalletSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </AppLayout>
  );
};
